import { dayjs } from '@lba-dev/package.local-globals/dayjs';
import { DEPANNAGE } from '@lba-dev/package.local-globals/calendarSources';

import notifSystem from '../../../notifSystem';
import * as service from '../../GestionLeadsCalendar/service';

// Calculer le total des pourcentages
export const getTotalPercentage = (distributions) =>
  distributions.reduce((sum, dist) => {
    const percentage = dist.percentage === '' ? 0 : dist.percentage;
    return sum + percentage;
  }, 0);

// Obtenir le label du jour
export const getDayLabel = (block, slotInfo) => {
  const date = block ? dayjs(block.start) : dayjs(slotInfo.start);
  return date.format('dddd D MMMM YYYY');
};

// Trouver les blocs qui chevauchent ce créneau, sur ce calendrier
// ou sur un autre calendrier à RDV (le Dépannage n'a pas de RDV)
export const getOverlappingBlocks = ({
  block,
  slotInfo,
  existingBlocks,
  calendrier,
  startTime,
  endTime,
}) => {
  const currentDayDate = block ? dayjs(block.start) : dayjs(slotInfo.start);
  const currentDay = currentDayDate.day();

  return existingBlocks.filter(b => {

    if (block && b.id === block.id) {
      return false;
    }

    if (b.day !== currentDay) {
      return false;
    }

    if (
      b.calendrier !== calendrier &&
      [b.calendrier, calendrier].includes(DEPANNAGE)
    ) {
      return false;
    }

    return startTime < b.endTime && b.startTime < endTime;
  });
};

// Obtenir les commerciaux possibles pour un créneau
export const getPossibleCommerciauxForSlot = ({
  distributions,
  commerciaux,
  currentUserId,
  block,
  slotInfo,
  existingBlocks,
  calendrier,
  startTime,
  endTime,
}) => {
  // 1. Users déjà sélectionnés dans la liste courante (celle du dialog ouvert)
  const usedUserIds = distributions
    .map(d => d.userId)
    .filter(id => id && id !== currentUserId);

  // 2. Trouver les users DÉJÀ assignés à la même heure, sur ce calendrier
  // ou sur un autre calendrier à RDV (le Dépannage n'a pas de RDV)
  const usersAlreadyAssignedAtThisTime = getOverlappingBlocks({
    block,
    slotInfo,
    existingBlocks,
    calendrier,
    startTime,
    endTime,
  }).flatMap(b => b.distributions.map(d => d.userId));

  return commerciaux.filter(c =>
    !usedUserIds.includes(c._id) &&
    (c._id === currentUserId ||
      !usersAlreadyAssignedAtThisTime.includes(c._id))
  );
};

// Obtenir les calendriers possibles pour un créneau
export const getPossibleCalendriersForSlot = ({
  block,
  slotInfo,
  existingBlocks,
  startTime,
  endTime,
  calendriers,
}) => {
  const currentDayDate = block ? dayjs(block.start) : dayjs(slotInfo.start);
  const currentDay = currentDayDate.day();

  const usedCalendriers = existingBlocks
    .filter(b => {
      if (block && b.id === block.id) {
        return false;
      }
      if (b.day !== currentDay) {
        return false;
      }
      return startTime < b.endTime && b.startTime < endTime;
    })
    .map(b => b.calendrier);

  return calendriers.filter(cal => !usedCalendriers.includes(cal));
};

// Valider le bloc
export const validateBlock = ({
  startTime,
  endTime,
  block,
  slotInfo,
  existingBlocks,
  calendrier,
  distributions,
  commerciaux = [],
}) => {
  // Vérifier si l'heure de début est avant l'heure de fin
  if (startTime >= endTime) {
    notifSystem.error(
      'Horaire invalide',
      'L\'heure de début doit être avant l\'heure de fin'
    );
    return false;
  }

  // Vérifier si le créneau chevauche un créneau existant DU MÊME CALENDRIER
  const currentDayDate = block ? dayjs(block.start) : dayjs(slotInfo.start);
  const currentDay = currentDayDate.day();

  const blocksToCheck = existingBlocks.filter(b => {
    if (block && b.id === block.id) {
      return false;
    }
    return b.day === currentDay && b.calendrier === calendrier;
  });

  const overlappingBlock = blocksToCheck.find(existingBlock =>
    startTime < existingBlock.endTime &&
      existingBlock.startTime < endTime
  );

  if (overlappingBlock) {
    notifSystem.error(
      'Conflit de créneau',
      `Un créneau "${calendrier}" existe déjà de 
      ${overlappingBlock.startTime} à ${overlappingBlock.endTime}`
    );
    return false;
  }

  // Vérifier si au moins un commercial est assigné
  if (distributions.length === 0) {
    notifSystem.error('ERREUR', 'Ajoutez au moins un commercial');
    return false;
  }

  // Vérifier si un commercial est assigné par attribution
  const hasEmptyUser = distributions.some(d => !d.userId);
  if (hasEmptyUser) {
    notifSystem.error(
      'ERREUR',
      'Toute attribution doit avoir un commercial assigné'
    );
    return false;
  }

  // Vérifier qu'aucun commercial n'est déjà placé sur un autre créneau
  // (même calendrier ou calendrier à RDV) qui chevauche celui-ci
  const overlappingBlocks = getOverlappingBlocks({
    block,
    slotInfo,
    existingBlocks,
    calendrier,
    startTime,
    endTime,
  });

  for (const b of overlappingBlocks) {
    const conflictDist = b.distributions.find(d =>
      distributions.some(dist => dist.userId === d.userId)
    );

    if (conflictDist) {
      const commercial = commerciaux.find(c => c._id === conflictDist.userId);
      const login = commercial?.login || 'Un commercial';
      notifSystem.error(
        'Conflit de commercial',
        `${login} est déjà placé sur "${b.calendrier}" ` +
          `de ${b.startTime} à ${b.endTime}`
      );
      return false;
    }
  }

  // Vérifier si la somme des pourcentages est égale à 100%
  const totalPercentage = getTotalPercentage(distributions);

  if (totalPercentage !== 100) {
    notifSystem.error(
      'Pourcentages invalides',
      `Le total doit être 100% (actuellement ${totalPercentage}%). ${
        totalPercentage < 100
          ? `Il manque ${100 - totalPercentage}%`
          : `Vous avez ${totalPercentage - 100}% en trop`
      }`
    );
    return false;
  }

  return true;
};

export const openReassignDialog = (
  { userId, slot, userLogin, onRefreshData },
  dispatch,
  setDialog
) => {
  dispatch(setDialog({
    name: 'ReassignLeadsDialog',
    open: true,
    dialogProps: {
      title: `Réattribuer les demandes de: ${userLogin}`,
      maxWidth: 'sm',
      fullWidth: true,
    },
    contentProps: {
      slot,
    },
    actions: [
      {
        children: 'Confirmer la réattribution',
        color: 'warning',
        onClick: async (contentProps, closeDialog) => {
          closeDialog();

          try {
            const { data: result } = await service
              .reassignSlotLeads(userId, slot);

            if (result.success) {
              notifSystem.success('SUCCÈS', result.message);
              if (onRefreshData) {
                await onRefreshData();
              }
            } else {
              notifSystem.error('ERREUR',
                result.message || 'Échec de la réattribution');
            }
          } catch (error) {
            notifSystem.error(
              'ERREUR',
              'Une erreur est survenue lors de la réattribution'
            );
          }
        },
      },
    ],
  }));
};

